/** Human PT-BR labels for technical domain values. Never render enums/slugs raw. */

function asKey(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

export function labelOf(map: Record<string, string>, value: unknown, fallback = '—'): string {
  const key = asKey(value);
  if (!key) return fallback;
  return map[key] ?? fallback;
}

// ── Person / membership ─────────────────────────────────

export const PERSON_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Ativo',
  AWAY: 'Ausente',
  SUSPENDED: 'Suspenso',
  DISCONNECTED: 'Desligado',
  DECEASED: 'Falecido',
  ARCHIVED: 'Arquivado',
};

export const MEMBERSHIP_TYPE_LABELS: Record<string, string> = {
  MEMBER: 'Membro(a)',
  COLLABORATOR: 'Colaborador(a)',
  COORDINATION: 'Coordenação',
  DIRECTOR: 'Direção',
  VISITOR: 'Visitante',
  SUPPORT: 'Apoio',
  OTHER: 'Outro',
};

export const MEMBERSHIP_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  SUSPENDED: 'Suspenso',
};

// ── System roles (slug → display) ───────────────────────

export const SYSTEM_ROLE_LABELS: Record<string, string> = {
  house_admin: 'Administrador(a) da casa',
  house_manager: 'Gestor(a) operacional',
  community_manager: 'Gestor(a) de comunidade',
  viewer: 'Visualizador(a)',
};

export function roleDisplayName(name: unknown, fallback?: string): string {
  const key = asKey(name);
  if (!key) return fallback ?? '—';
  return SYSTEM_ROLE_LABELS[key] ?? (key.includes('_') ? (fallback ?? 'Papel personalizado') : key);
}

// ── Permission keys ─────────────────────────────────────

export const PERMISSION_DOMAIN_LABELS: Record<string, string> = {
  house: 'Casa',
  community: 'Comunidade',
  roles: 'Papéis',
  calendar: 'Calendário',
  obligations: 'Obrigações',
  tasks: 'Tarefas',
  inventory: 'Estoque',
  finance: 'Financeiro',
  reports: 'Relatórios',
  documents: 'Documentos',
  audit: 'Auditoria',
  member_app: 'App do membro',
  functions: 'Cargos e funções',
  people: 'Pessoas',
};

export const PERMISSION_LABELS: Record<string, string> = {
  'house.read': 'Ver dados da casa',
  'house.update': 'Editar dados da casa',
  'house.settings.read': 'Ver configurações da casa',
  'house.settings.update': 'Editar configurações da casa',
  'community.people.read': 'Ver pessoas',
  'community.people.create': 'Cadastrar pessoas',
  'community.people.update': 'Editar pessoas',
  'community.people.status.change': 'Alterar situação de pessoas',
  'community.membership.read': 'Ver vínculos',
  'community.membership.update': 'Editar vínculos',
  'roles.read': 'Ver papéis de acesso',
  'roles.create': 'Criar papéis de acesso',
  'roles.update': 'Editar papéis de acesso',
  'roles.assign': 'Atribuir papéis de acesso',
  'roles.permissions.read': 'Ver permissões dos papéis',
  'roles.permissions.update': 'Editar permissões dos papéis',
  'roles.effective.read': 'Ver permissões efetivas',
  'calendar.events.read': 'Ver eventos',
  'calendar.events.create': 'Criar eventos',
  'calendar.events.update': 'Editar eventos',
  'calendar.events.cancel': 'Cancelar eventos',
  'calendar.events.restricted.read': 'Ver eventos restritos',
  'obligations.read': 'Ver obrigações',
  'obligations.restricted.read': 'Ver obrigações restritas',
  'obligations.create': 'Criar obrigações',
  'obligations.update': 'Editar obrigações',
  'obligations.status.change': 'Alterar situação de obrigações',
  'obligations.responsibles.manage': 'Gerenciar responsáveis de obrigações',
  'obligations.archive': 'Arquivar obrigações',
  'tasks.read': 'Ver tarefas',
  'tasks.create': 'Criar tarefas',
  'tasks.update': 'Editar tarefas',
  'tasks.status.change': 'Alterar situação de tarefas',
  'tasks.assign': 'Atribuir tarefas',
  'tasks.checklist.manage': 'Gerenciar lista de verificação',
  'tasks.archive': 'Arquivar tarefas',
  'inventory.items.read': 'Ver itens de estoque',
  'inventory.items.create': 'Cadastrar itens de estoque',
  'inventory.items.update': 'Editar itens de estoque',
  'inventory.movements.read': 'Ver movimentações de estoque',
  'inventory.movements.create': 'Registrar movimentações de estoque',
  'inventory.adjustments.create': 'Registrar ajustes de estoque',
  'inventory.alerts.read': 'Ver alertas de estoque',
  'inventory.restricted.read': 'Ver estoque restrito',
  'inventory.categories.manage': 'Gerenciar categorias de estoque',
  'inventory.locations.manage': 'Gerenciar localizações de estoque',
  'finance.entries.read': 'Ver lançamentos financeiros',
  'finance.entries.create': 'Criar lançamentos financeiros',
  'finance.entries.update': 'Editar lançamentos financeiros',
  'finance.entries.cancel': 'Cancelar lançamentos financeiros',
  'finance.entries.status.change': 'Alterar situação financeira',
  'finance.summary.read': 'Ver resumo financeiro',
  'finance.contributions.read': 'Ver contribuições',
  'finance.contributions.manage': 'Gerenciar contribuições',
  'finance.categories.manage': 'Gerenciar categorias financeiras',
  'reports.dashboard.read': 'Ver painel de relatórios',
  'reports.tasks.read': 'Ver relatórios de tarefas',
  'reports.obligations.read': 'Ver relatórios de obrigações',
  'reports.inventory.read': 'Ver relatórios de estoque',
  'reports.finance.read': 'Ver relatórios financeiros',
  'reports.accountability.read': 'Ver prestação de contas',
  'documents.read': 'Ver documentos',
  'documents.create': 'Enviar documentos',
  'documents.download': 'Baixar documentos',
  'documents.archive': 'Arquivar documentos',
  'documents.update': 'Editar documentos',
  'member_app.access': 'Acessar app do membro',
  'member_app.profile.read': 'Ver perfil no app',
  'member_app.profile.update': 'Atualizar perfil no app',
  'member_app.tasks.read': 'Ver tarefas no app',
  'member_app.tasks.respond': 'Responder tarefas no app',
  'member_app.calendar.read': 'Ver agenda no app',
  'member_app.messages.read': 'Ver avisos no app',
  'member_app.messages.acknowledge': 'Confirmar ciência de avisos',
  'member_app.attendance.respond': 'Confirmar presença no app',
  'member_app.preferences.manage': 'Gerenciar preferências do app',
  'member_app.notifications.manage': 'Gerenciar notificações do app',
  'member_app.devices.manage': 'Gerenciar dispositivos do app',
  'communication.messages.read': 'Ver avisos internos',
  'communication.messages.create': 'Criar avisos internos',
  'communication.messages.manage': 'Gerenciar avisos internos',
  'calendar.events.app_visibility.manage': 'Configurar visibilidade no app',
  'obligations.app_communication.manage': 'Configurar comunicação de obrigações no app',
  'functions.read': 'Ver cargos e funções',
  'functions.manage': 'Gerenciar cargos e funções',
  'functions.assign': 'Atribuir cargos e funções',
  'people.oriki.read': 'Ver Oríkì',
  'people.oriki.update': 'Editar Oríkì',
  'audit.core.read': 'Ver auditoria',
};

export const TASK_ASSIGNMENT_STATUS_LABELS: Record<string, string> = {
  ASSIGNED: 'Designada',
  ACKNOWLEDGED: 'Ciência confirmada',
  IN_PROGRESS: 'Em andamento',
  DONE: 'Concluída',
  BLOCKED: 'Impedida',
  DECLINED: 'Recusada',
};

export const ATTENDANCE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  CONFIRMED: 'Confirmada',
  DECLINED: 'Recusada',
};

export const DOCUMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativo',
  ARCHIVED: 'Arquivado',
  EXPIRED: 'Vencido',
};

export const DOCUMENT_VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Público',
  INTERNAL: 'Interno',
  RESTRICTED: 'Restrito',
};

export function permissionLabel(key: unknown): string {
  const k = asKey(key);
  if (!k) return '—';
  return PERMISSION_LABELS[k] ?? 'Permissão personalizada';
}

export function permissionDomainLabel(key: unknown): string {
  const k = asKey(key);
  const domain = k.split('.')[0] ?? '';
  return PERMISSION_DOMAIN_LABELS[domain] ?? 'Outros';
}

// ── Tasks ───────────────────────────────────────────────

export const TASK_STATUS_LABELS: Record<string, string> = {
  TODO: 'A fazer',
  IN_PROGRESS: 'Em andamento',
  BLOCKED: 'Bloqueada',
  DONE: 'Concluída',
  CANCELLED: 'Cancelada',
  OVERDUE: 'Atrasada',
};

export const TASK_PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
};

export const TASK_CATEGORY_LABELS: Record<string, string> = {
  CLEANING: 'Limpeza',
  KITCHEN: 'Cozinha',
  PURCHASE: 'Compra',
  TRANSPORT: 'Transporte',
  RECEPTION: 'Recepção',
  MAINTENANCE: 'Manutenção',
  DECORATION: 'Decoração',
  COMMUNICATION: 'Comunicação',
  ADMINISTRATIVE: 'Administrativo',
  EVENT_SUPPORT: 'Apoio a evento',
  INVENTORY_CHECK: 'Conferência de estoque',
  SPACE_PREPARATION: 'Preparação de espaço',
  OTHER: 'Outro',
};

// ── Calendar ────────────────────────────────────────────

export const EVENT_TYPE_LABELS: Record<string, string> = {
  PUBLIC_FESTIVAL: 'Festividade pública',
  INTERNAL_OBLIGATION: 'Obrigação interna',
  MEETING: 'Reunião',
  MUTIRAO: 'Mutirão',
  CLEANING: 'Limpeza',
  MAINTENANCE: 'Manutenção',
  PURCHASE: 'Compra',
  ATTENDANCE: 'Atendimento',
  HOUSE_ANNIVERSARY: 'Aniversário da casa',
  DOCUMENT_DEADLINE: 'Prazo de documento',
  OTHER: 'Outro',
};

export const EVENT_STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Agendado',
  IN_PREPARATION: 'Em preparação',
  HAPPENING: 'Acontecendo',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
  POSTPONED: 'Adiado',
  ARCHIVED: 'Arquivado',
};

export const EVENT_VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Público',
  INTERNAL: 'Interno',
  RESTRICTED: 'Restrito',
};

// ── Obligations ─────────────────────────────────────────

export const OBLIGATION_STATUS_LABELS: Record<string, string> = {
  PLANNED: 'Planejada',
  IN_PREPARATION: 'Em preparação',
  PENDING_DECISION: 'Aguardando decisão',
  PENDING_MATERIALS: 'Aguardando materiais',
  PENDING_FINANCE: 'Aguardando financeiro',
  PENDING_PEOPLE: 'Aguardando pessoas',
  SCHEDULED: 'Agendada',
  COMPLETED: 'Concluída',
  POSTPONED: 'Adiada',
  CANCELLED: 'Cancelada',
  ARCHIVED: 'Arquivada',
  RESTRICTED_ARCHIVED: 'Arquivada (restrita)',
};

export const OBLIGATION_TYPE_LABELS: Record<string, string> = {
  PERSON_OBLIGATION: 'Obrigação de pessoa',
  HOUSE_OBLIGATION: 'Obrigação da casa',
  TIME_CYCLE: 'Ciclo temporal',
  ANNUAL_OBLIGATION: 'Obrigação anual',
  ROLE_OBLIGATION: 'Obrigação de cargo',
  INITIATION_RELATED: 'Relacionada a iniciação',
  ANCESTOR_RELATED: 'Relacionada a ancestrais',
  CALENDAR_RELATED: 'Relacionada ao calendário',
  EMERGENCY: 'Emergência',
  OTHER: 'Outra',
};

export const OBLIGATION_VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Pública',
  INTERNAL: 'Interna',
  RESTRICTED: 'Restrita',
};

// ── Inventory ───────────────────────────────────────────

export const INVENTORY_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativo',
  LOW_STOCK: 'Estoque baixo',
  OUT_OF_STOCK: 'Sem estoque',
  EXPIRED: 'Vencido',
  INACTIVE: 'Inativo',
  ARCHIVED: 'Arquivado',
};

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  INCOMING: 'Entrada',
  OUTGOING: 'Saída',
  ADJUSTMENT: 'Ajuste',
  LOSS_DISPOSAL: 'Perda / descarte',
};

export const INVENTORY_VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Público',
  INTERNAL: 'Interno',
  RESTRICTED: 'Restrito',
};

// ── Finance ─────────────────────────────────────────────

export const FINANCE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PAID: 'Pago',
  CANCELLED: 'Cancelado',
  OVERDUE: 'Atrasado',
  REFUNDED: 'Estornado',
  ARCHIVED: 'Arquivado',
};

export const FINANCE_TYPE_LABELS: Record<string, string> = {
  INCOME: 'Receita',
  EXPENSE: 'Despesa',
  CONTRIBUTION: 'Contribuição',
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Dinheiro',
  CREDIT_CARD: 'Cartão de crédito',
  DEBIT_CARD: 'Cartão de débito',
  PIX: 'Pix',
  TRANSFER: 'Transferência',
  BANK_SLIP: 'Boleto',
  OTHER: 'Outro',
};

// ── House ───────────────────────────────────────────────

export const HOUSE_STATUS_LABELS: Record<string, string> = {
  SETUP: 'Em configuração',
  ACTIVE: 'Ativa',
  SUSPENDED: 'Suspensa',
  ARCHIVED: 'Arquivada',
};

export const MODULE_LABELS: Record<string, string> = {
  house: 'Casa',
  community: 'Comunidade',
  roles: 'Permissões',
  calendar: 'Calendário',
  obligations: 'Obrigações',
  tasks: 'Tarefas',
  inventory: 'Estoque',
  finance: 'Financeiro',
  documents: 'Documentos',
  reports: 'Relatórios',
};
